import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core'
import { MatProgressBar } from '@angular/material/progress-bar'
import { MatTableDataSource } from '@angular/material/table'
import { ActivatedRoute, Navigation, Router } from '@angular/router'
import {
  ApiService,
  AppointmentModel,
  Clinic,
  ClinicService,
  ConfirmService,
  CrudService,
  EstimateModel,
  Facility,
  FacilityService,
  FileService,
  Invoice,
  InvoiceLineItem,
  InvoiceModel,
  ParamsService,
  Patient,
  PatientDataService,
  PatientPalRestError,
  PaymentResponse,
  ProgressIndicatorService,
  RestResponse,
  TransactionModel,
} from '@mmx/shared'
import { TranslateService } from '@ngx-translate/core'
import { extend, has, pull } from 'lodash'
import moment from 'moment'
import { combineLatest, Observable, of, Subject } from 'rxjs'
import { catchError, filter, takeUntil, tap } from 'rxjs/operators'

import {
  SendContactEditDialogResult,
  SendContactEditDialogService,
} from '../send-contact-edit-dialog'
import { addRowAnimation } from './add-row-animation'

@Component({
  selector: 'mmx-invoice',
  templateUrl: './invoice.component.html',
  animations: [addRowAnimation],
  standalone: false,
})
export class InvoiceComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild(MatProgressBar) progressBar: MatProgressBar
  /** Optional invoice id when using the component outside of a router context */
  @Input() embedded = false
  @Input() invoiceId?: string

  clinic: Clinic
  public patient: Patient
  public invoice: InvoiceModel
  public appointments: AppointmentModel[]
  public location: Facility | undefined

  /**
   * If this invoice is not editable and assigned to an appointment,
   * this will hold the appointment information so we can add it to the breadcrumbs
   */
  public appointment: AppointmentModel
  public estimate: EstimateModel
  public transactions: TransactionModel[]
  public items: MatTableDataSource<InvoiceLineItem>
  public STATUS = Invoice.STATUS

  private onDestroy$ = new Subject<void>()
  private pendingParams?: { id?: string }

  public get columnsToDisplay() {
    if (this.editable) {
      return ['description', 'quantity', 'unit_amount', 'amount', 'actions']
    } else {
      return ['description', 'quantity', 'unit_amount', 'amount']
    }
  }

  public get editable() {
    return this.invoice && this.invoice.status === Invoice.STATUS.DRAFT
  }

  public get hasDefaultTax() {
    return this.clinicService.clinic.defaultTax
  }

  public get hasTax() {
    return (
      this.invoice &&
      (this.invoice.tax > 0 || this.invoice.taxAmounts.length > 0)
    )
  }

  public get paymentPlansEnabled(): boolean {
    return this.clinicService.clinic.modulesPreferences.paymentPlans
  }

  public get promptPayDiscountEnabled(): boolean {
    return this.clinicService.clinic.modulesPreferences.promptPayments
  }

  private navigation: Navigation | void

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private clinicService: ClinicService,
    private sendContactEditDialogService: SendContactEditDialogService,
    private facilityService: FacilityService,
    private fileService: FileService,
    private patientDataService: PatientDataService,
    private restService: ApiService,
    private crudService: CrudService,
    private progressService: ProgressIndicatorService,
    private confirmService: ConfirmService,
    private paramsService: ParamsService,
    private translate: TranslateService,
  ) {
    this.navigation = this.router.getCurrentNavigation()
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes.invoiceId &&
      !changes.invoiceId.firstChange &&
      changes.invoiceId.currentValue &&
      changes.invoiceId.currentValue !== changes.invoiceId.previousValue
    ) {
      this.loadFromParams({ id: changes.invoiceId.currentValue })
    }
  }

  ngOnInit() {
    this.clinic = this.clinicService.clinic
    this.patientDataService.patient$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((patient) => {
        this.patient = patient

        if (this.pendingParams) {
          this.loadFromParams(this.pendingParams)
          this.pendingParams = undefined
        }
      })

    this.facilityService.current$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((location) => {
        this.location = location
      })

    const params$: Observable<{ id?: string }> = this.invoiceId
      ? of({ id: this.invoiceId })
      : this.route.params
    params$.pipe(takeUntil(this.onDestroy$)).subscribe((params) => {
      this.loadFromParams(params)
    })
  }

  ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  private loadFromParams(params: { id?: string }) {
    if (!params || params.id === this.invoice?.id) {
      return
    }

    if (!this.patient) {
      this.pendingParams = params
      return
    }

    if (params.id && params.id !== 'new') {
      combineLatest([
        this.restService
          .getInvoice(params.id)
          .pipe(takeUntil(this.onDestroy$)),
        this.restService
          .getPatientTransactions(this.patient.id, { invoice: params.id })
          .pipe(takeUntil(this.onDestroy$)),
      ]).subscribe(
        ([invoice, transactions]) => {
          this.invoice = invoice
          this.transactions = transactions
          this.onLoad()
        },
        () => {
          // if the invoice doesn't exist, redirect to patient details
          this.router.navigate(['patient', this.patient.id])
        },
      )
    } else {
      // look for any data we should use for this new invoice
      const invoiceData = this.navigation ? this.navigation.extras.state : {}

      this.invoice = new InvoiceModel(
        extend(
          {
            status: Invoice.STATUS.DRAFT,
            patientId: this.patient.id,
            currency: this.clinicService.clinic.settings.defaultCurrency,
            amountDue: 0,
            amountPaid: 0,
            amountRemaining: 0,
            lineItems: [],
            attemptCount: 0,
            subtotal: 0,
            tax: 0,
            taxAmounts: this.clinicService.clinic.defaultTax
              ? [
                {
                  amount: 0,
                  taxRateId: this.clinicService.clinic.defaultTax.id,
                  taxRate: this.clinicService.clinic.defaultTax,
                },
              ]
              : [],
            total: 0,
            options: {
              enablePaymentPlan: this.paymentPlansEnabled,
              enablePromptPayDiscount: this.promptPayDiscountEnabled,
            },
            dueDate: moment().add(30, 'days'),
          },
          invoiceData,
        ),
      )

      this.onLoad()
    }
  }

  onDueDaysChange(dueDays: string) {
    this.invoice.dueDate = moment().add(parseInt(dueDays), 'days')
  }

  save(message?: string) {
    this.invoice.lineItems = this.items.data
    const progress = this.progressService.start()
    const data = this.invoice.toJSON()
    let obs: Observable<RestResponse<any>>

    if (this.invoice.id) {
      obs = this.restService.put<any>(`/invoices/${this.invoice.id}`, data)
    } else {
      obs = this.restService.post<any>('/invoices', data)
    }

    obs.pipe(takeUntil(this.onDestroy$)).subscribe((resp) => {
      progress.stop(message || 'Saved Invoice')

      if (!this.invoice.id) {
        this.invoice.id = resp.data.id
        this.router.navigate([
          'patient',
          this.invoice.patientId,
          'invoices',
          this.invoice.id,
        ])
      }
    })
  }

  delete() {
    const progress = this.progressService.start()

    this.restService
      .delete(`/invoices/${this.invoice.id}`)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        progress.stop(this.editable ? 'Deleted Invoice' : 'Voided Invoice')

        // if the invoice was deleted, and not just voided, redirect the user
        if (this.editable) {
          this.router.navigate(['patient', this.invoice.patientId])
        } else {
          this.invoice.status = Invoice.STATUS.VOID
        }
      })
  }

  void() {
    // Allow only if there is no payments
    if (this.invoice.amountPaid === 0) {
      // ask for void reason
      this.confirmService
        .confirm(`Are you sure you want to void this invoice?`)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((confirmation) => {
          if (confirmation) {
            this.delete()
          }
        })
    } else {
      const progress = this.progressService.start()
      progress.stop(
        'Please refund the remaining transactions before void an invoice',
      )
    }
  }

  confirm() {
    this.invoice.status =
      this.invoice.total === 0 ? Invoice.STATUS.PAID : Invoice.STATUS.CONFIRMED
    this.invoice.collectionMethod = Invoice.COLLECTION_METHOD.UPCOMING
    this.save('Confirmed Invoice')
  }

  onLoad() {
    if (this.editable) {
      if (!this.invoice.currency) {
        this.invoice.currency =
          this.clinicService.clinic.settings.defaultCurrency
      }

      if (this.invoice.lineItems.length === 0) {
        this.invoice.lineItems = [this.newLineItem()]
      } else {
        this.invoice.lineItems = this.invoice.lineItems.map((item) => {
          if (!item.currency) {
            item.currency = this.invoice.currency
          }

          if (!item.quantity && !item.unit_amount) {
            item.quantity = 1
            item.unit_amount = has(item, 'amount') ? item.amount : 0
          }

          return item
        })
      }
    } else if (this.invoice.appointmentId) {
      this.loadAppointment(this.invoice.appointmentId)
    }

    if (this.invoice?.estimateId) {
      this.loadEstimate()
    }

    this.items = new MatTableDataSource(this.invoice.lineItems)
    // if this invoice is still editable, make sure all calculations are correct
    if (this.editable) {
      this.items.data.forEach((item) => this.sumTotal(item))
    }

    // if we have now attempted to charge 3 times, notify patient
    if (this.invoice.attemptCount >= 3) {
      this.progressService.snack({
        message: this.translate.instant('invoice-card.payment-failed'),
        panelClass: 'warn',
      })
    }
  }

  addLineItem() {
    if (this.invoice) {
      this.items.data.push(this.newLineItem())
      this.items.filter = ''
    }
  }

  removeLineItem(item: InvoiceLineItem) {
    pull(this.items.data, item)
    this.items.filter = ''
  }

  newLineItem(): InvoiceLineItem {
    return {
      description: '',
      amount: 0,
      unit_amount: 0,
      quantity: 1,
      currency: this.clinicService.clinic.settings.defaultCurrency,
      // taxRates: [],
      discountable: true,
    }
  }

  sumTotal(item: InvoiceLineItem) {
    item.amount = item.unit_amount * item.quantity
    this.invoice.subtotal = this.items.data
      .map((i) => i.amount)
      .reduce((a, b) => a + b, 0)
    if (this.location && this.location.defaultTaxes) {
      this.invoice.taxes = this.location.defaultTaxes
    }
    this.invoice.total = this.invoice.subtotal + this.invoice.tax
    this.invoice.amountDue = this.invoice.total
    this.invoice.amountRemaining =
      this.invoice.amountDue - (this.invoice.amountPaid || 0)
  }

  // Set the patient responsibility (amount patient owes) from the estimate to the payment
  newUpdateFromCharge(data: PaymentResponse): void {
    if (data.transaction) {
      this.transactions.unshift(new TransactionModel(data.transaction))
      this.transactions = this.transactions.slice()
    }

    if (data.invoice) {
      this.invoice = new InvoiceModel(data.invoice)
      this.paramsService.setPatientId(this.invoice.patientId)
    }
  }

  download() {
    this.progressBar.mode = 'indeterminate'
    this.restService
      .generateInvoicePDF(this.invoice.id)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (resp) => {
          this.fileService.saveAs(resp.data)
          this.progressBar.mode = 'determinate'
        },
        () => {
          this.progressBar.mode = 'determinate'
          this.progressService.snack(
            'An error occurred while trying to download the intake document.',
          )
        },
      )
  }

  canSendEmail(invoice: InvoiceModel): boolean {
    return !!this.patient?.canEmail && invoice?.status !== Invoice.STATUS.PAID
  }

  canSendSms(invoice: InvoiceModel): boolean {
    return !!this.patient?.canText && invoice?.status !== Invoice.STATUS.PAID
  }

  private loadAppointment(id: string) {
    this.restService.getAppointment(id)
      .pipe(
        takeUntil(this.onDestroy$),
        tap(appointment => {
          this.appointment = appointment
        }),
      ).subscribe()
  }

  private loadEstimate() {
    this.crudService.read(EstimateModel, this.invoice.estimateId)
      .pipe(
        takeUntil(this.onDestroy$),
        catchError((error) => {
          throw error
        }),
        tap(estimate => {
          this.estimate = estimate
        }),
      ).subscribe()
  }

  openSendDialog(what: 'link' | 'pdf', method?: 'email' | 'sms') {
    this.sendContactEditDialogService.open({
      method: method ?? 'email',
      phone: this.patient?.phone,
      email: this.patient?.email,
    })
      .pipe(
        takeUntil(this.onDestroy$),
        filter(Boolean),
      )
      .subscribe((result: SendContactEditDialogResult) => {
        this.sendInvoice(what, method, result.to)
      })
  }

  sendInvoice(what: 'link' | 'pdf', method?: 'email' | 'sms', to?: string) {
    const progress = this.progressService.start()
    const facilityId = this.location?.id
    const params = { what, method, facilityId, to }
    this.progressBar.mode = 'indeterminate'
    this.restService
      .sendInvoiceMessage(this.invoice.id, params)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: () => {
          progress.stop(`Invoice ${what} has been sent`)
          this.progressBar.mode = 'determinate'
        },
        error: (err: PatientPalRestError) => {
          const message = err.error?.message || 'Please try again.'
          progress.stop(`Failed to send invoice ${what}. ${message}`)
          this.progressBar.mode = 'determinate'
        },
      })
  }
}
